import { useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';

import { MENU_OPTIONS } from 'src/constants/menu';
import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { usePathname } from 'src/routes/hooks';
import { UserRole } from 'src/type';

import DialogLogout from './dialog-logout';

export default function AccountPopover() {
  const theme = useTheme();
  const pathname = usePathname();

  const { loading, user, setCurrentRole } = useAuthContext();

  const { t: tMenu } = useTranslate('sidebar');
  const { t: tSystem } = useTranslate('system');
  const { t: tFields } = useTranslate('fields');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [groupedRoles, setGroupedRoles] = useState<Record<string, UserRole[]>>({});

  const [expandedOrg, setExpandedOrg] = useState<string | false>(false);

  // เพิ่มฟังก์ชันสำหรับเรียงลำดับ roles
  const sortRoles = (roles: UserRole[]) => {
    const roleOrder = ['USER', 'GARBAGE_COLLECTOR', 'NODE_OFFICER', 'MANAGER', 'ADMIN', 'SYSTEM'];
    return [...roles].sort((a, b) => {
      const indexA = roleOrder.indexOf(a.role);
      const indexB = roleOrder.indexOf(b.role);
      return indexA - indexB;
    });
  };

  useEffect(() => {
    setTimeout(() => {
      const groupedRolesTemp = user?.userRoles?.reduce(
        (acc, role) => {
          const orgName =
            role?.organize?.name || role.organizeData?.name || tFields('selectOrganize');
          if (!acc[orgName]) acc[orgName] = [];
          acc[orgName].push(role);
          return acc;
        },
        {} as Record<string, UserRole[]>
      );

      // เรียงลำดับ roles ในแต่ละ organization
      if (groupedRolesTemp) {
        Object.keys(groupedRolesTemp).forEach((orgName) => {
          groupedRolesTemp[orgName] = sortRoles(groupedRolesTemp[orgName]);
        });
        setGroupedRoles(groupedRolesTemp);
      }

      const activeRoleOrg =
        groupedRolesTemp &&
        Object.entries(groupedRolesTemp).find(([orgName, roles]) =>
          roles.some((role) => role.id === user?.currentRole?.currentRole?.id)
        );

      if (activeRoleOrg) {
        setExpandedOrg(activeRoleOrg[0]);
      }
    }, 500);
  }, [user, roleMenuAnchor]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenRoleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setRoleMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setRoleMenuAnchor(null);
  };

  const handleRoleChange = (role: UserRole) => {
    if (role.id) setCurrentRole(role.id);
    handleClose();
  };

  const handleToggleAccordion = (orgName: string) => {
    setExpandedOrg((prev) => (prev === orgName ? false : orgName));
  };

  return (
    <Stack
      className="account-popover-custom"
      component="nav"
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ height: 1 }}
    >
      {loading ? (
        <Skeleton variant="circular" width={32} height={32} />
      ) : (
        <>
          {user && (
            <>
              <List component="nav" sx={{ px: 0 }}>
                <ListItemButton
                  id="lock-button"
                  aria-haspopup="listbox"
                  onClick={handleClickListItem}
                  sx={{ borderRadius: 1, gap: 1.5 }}
                >
                  <ListItemText
                    primary={user?.name}
                    secondary={user?.role}
                    primaryTypographyProps={{
                      typography: 'body1',
                      noWrap: true,
                      sx: { fontWeight: 'bold' },
                    }}
                    secondaryTypographyProps={{
                      component: 'span',
                      typography: 'caption',
                      sx: { fontSize: '10.5px', fontWeight: 'bold' },
                    }}
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                  />
                  <Avatar
                    alt="Avatar"
                    src={user?.imageUrl || ''}
                    sx={{ width: 40, height: 40, color: '#fff' }}
                  />
                </ListItemButton>
              </List>

              {/* Profile Menu */}
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'user-button', role: 'listbox' }}
                sx={{ mt: 1 }}
              >
                {/* User Info */}
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                  <Stack direction="column">
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {user?.name || 'Anonymous'}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', opacity: 0.6 }}>
                      {user?.email || ''}
                    </Typography>
                  </Stack>
                </Box>
                <Divider sx={{ my: 1 }} />

                {/* Role Selection Button (Opens Mega Menu) */}
                <Box sx={{ p: 0 }}>
                  <MenuItem
                    onClick={handleOpenRoleMenu}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}
                    >
                      <Iconify
                        icon="si:user-fill"
                        sx={{ opacity: 0.3, width: 20, height: 20, mr: 0.25 }}
                      />
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {user.currentRole?.currentRole?.role || ''}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', opacity: 0.6 }}>
                        ({user?.organizeData?.name})
                      </Typography>
                    </Stack>
                    <Iconify
                      icon="eva:arrow-ios-downward-fill"
                      sx={{ width: 20, height: 20, opacity: 0.4 }}
                    />
                  </MenuItem>
                </Box>

                {/* Navigation Menu */}
                {MENU_OPTIONS.map((option, index) => {
                  const isActive = pathname.replace(/\/$/, '') === option.value.replace(/\/$/, '');
                  const isSignOut = option.value === 'sign-out';

                  return (
                    <Box key={index}>
                      {isSignOut && <Divider sx={{ my: 1 }} />}
                      <MenuItem
                        selected={isActive}
                        onClick={() => setOpenDialog(true)}
                        sx={{
                          ...(isActive && {
                            bgcolor: theme.palette.action.selected,
                            color: theme.palette.primary.main,
                            fontWeight: 'bold',
                          }),
                          ...(isSignOut && {
                            color: theme.palette.error.dark,
                          }),
                        }}
                      >
                        <Stack
                          direction="row"
                          minWidth={180}
                          spacing={1}
                          alignItems="center"
                          sx={{
                            fontSize: 14,
                            fontWeight: isActive ? 600 : 500,
                          }}
                        >
                          <Iconify icon="hugeicons:logout-04" sx={{ width: 22, height: 22 }} />
                          {tMenu(option.lang)}
                        </Stack>
                      </MenuItem>
                    </Box>
                  );
                })}
              </Menu>

              {/* Organization & Role Selection */}
              <Menu
                id="role-menu"
                anchorEl={roleMenuAnchor}
                open={Boolean(roleMenuAnchor)}
                onClose={handleClose}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ mt: -5, minWidth: '250px' }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    borderRadius: 1,
                    opacity: 0.6,
                    fontWeight: 400,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  {tSystem('select.organize')}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {Object.entries(groupedRoles).map(([orgName, roles], index) => (
                  <Accordion
                    key={index}
                    expanded={expandedOrg === orgName}
                    onChange={() => handleToggleAccordion(orgName)}
                    sx={{
                      '&.Mui-expanded': {
                        border: '1px dashed grey',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                      sx={{
                        gap: 3,
                        m: 0,
                        '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 },
                        '&.Mui-expanded': { minHeight: 0 },
                      }}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{orgName}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1 }}>
                      {roles.map((role, idx) => {
                        const isActive = user.currentRole?.currentRole?.id === role.id;
                        return (
                          <MenuItem
                            key={idx}
                            selected={isActive}
                            onClick={() => handleRoleChange(role)}
                            sx={{ py: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                          >
                            {isActive ? (
                              <Iconify
                                icon="eva:checkmark-circle-2-fill"
                                width={20}
                                height={20}
                                color="primary.main"
                              />
                            ) : (
                              <Box sx={{ width: 20, height: 20 }} />
                            )}

                            <ListItemText primary={role.role} />
                          </MenuItem>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Menu>

              <DialogLogout open={openDialog} onClose={() => setOpenDialog(false)} />
            </>
          )}
        </>
      )}
    </Stack>
  );
}
